import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { CircularProgress } from '@material-ui/core';
import {
  Send,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  BookOpen,
  GitBranch,
  FileText,
  Layers,
  Paperclip,
  X,
  Trash2,
  Plus,
  Printer,
} from 'react-feather';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSelector } from 'react-redux';
import withAuthContext from '../../context/auth/AuthConsumer';
import { IAuthContext } from '../../context/auth/auth-context';
import mountainHeroBg from '../../assets/mountain-6.jpg';
import HeaderEnviroAILogo from '../../assets/header-enviroai-logo.svg';
import EnviroHeroChrome from '../../components/EnviroHeroChrome';
import RecentFavoriteSites from '../../components/RecentFavoriteSites';
import { findJurisdiction, JurisdictionId } from '../../types/jurisdiction';
import JurisdictionChip from '../../components/JurisdictionChip';
import { IJurisdictionState } from '../../store/jurisdiction/types';
import { exportChatTranscriptToPdf, formatDisplayDate, PdfChatMessage } from '../../utils/pdf';
import { FileDropOverlay } from '../../components/ui';
import useFileDrop from '../../hooks/useFileDrop';
import { ACCEPT_ATTR, IMAGE_MIME_TYPES, partitionFiles } from '../../utils/fileAccept';
import notifyRejectedFiles from '../../utils/notifyRejectedFiles';

// ─── Constants ────────────────────────────────────────────────────────────────

const MODELS = [
  { id: 'auto', label: 'Auto', description: 'Select the best available model' },
  { id: 'gemini', label: 'Gemini', description: 'Google' },
  { id: 'openai', label: 'GPT', description: 'OpenAI' },
  { id: 'claude', label: 'Claude', description: 'Anthropic' },
] as const;

type ModelId = (typeof MODELS)[number]['id'];

const AUTO_MODEL = MODELS[0];
const FRONTIER_MODELS = MODELS.slice(1);

const CLASSIFY_API = process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT;
// Which backend answers the chat. The Pi agent route (/enviro/agent/stream) speaks the
// same event stream as classify_route/stream; both take file uploads. The chat id goes
// along so the agent's working notes are scoped to this chat.
const CLASSIC_CHAT_ROUTE = '/enviro/classify_route/stream';
const CHAT_ROUTE = process.env.REACT_APP_ENVIROCHAT_CHAT_ROUTE || CLASSIC_CHAT_ROUTE;
const CERES_API = process.env.REACT_APP_CERES_URL;

const INTENT_ROUTES: Record<string, string> = {
  'enviro-multi-model': '/myenviroai',
  'enviro-search': '/search',
  'enviro-deep_research': '/enviro-deep-research',
  'enviro-permit': '/permit-agent',
};

const INTENT_META: Record<string, { label: string; subtitle: string }> = {
  'enviro-multi-model': { label: 'EnviroMulti-Model', subtitle: 'Ask anything' },
  'enviro-search': { label: 'Enviro AI Search', subtitle: '11M+ documents' },
  'enviro-deep_research': { label: 'Enviro Deep Search', subtitle: 'Multi-source analysis' },
  'enviro-permit': { label: 'Enviro Permit', subtitle: 'Permits & compliance' },
};

const INTENT_ICONS: Record<string, React.FC<{ size?: number }>> = {
  'enviro-multi-model': GitBranch,
  'enviro-search': BookOpen,
  'enviro-deep_research': Search,
  'enviro-permit': FileText,
};

const SUGGESTIONS_LABEL = 'Here are the best tools for your query…';

const MODEL_API_IDS: Record<string, string> = {
  auto: 'gpt-5.4-mini',
  openai: 'gpt-5.4-mini',
  claude: 'claude-sonnet-4-6',
  gemini: 'gemini-3-flash-preview',
};


const generateChatId = () => `ec-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const wave = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30%            { transform: translateY(-5px); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const PageContainer = styled.div`
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-image:
    linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.55) 0%,
      rgba(0, 0, 0, 0.65) 45%,
      rgba(0, 0, 0, 0.75) 100%
    ),
    url(${mountainHeroBg});
  background-size: cover;
  background-position: center 30%;
  background-repeat: no-repeat;
  background-attachment: fixed;
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const Sidebar = styled.div<{ $open: boolean }>`
  width: ${(p) => (p.$open ? '260px' : '0')};
  overflow: hidden;
  transition: width 0.25s ease;
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  border-right: 1px solid rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(16px);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarInner = styled.div`
  width: 260px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  @media (min-width: 769px) {
    padding-top: 62px;
    transition: padding-top 0.25s ease;

    body.ceres-main-sidebar-open & {
      padding-top: 0;
    }
  }
`;

const SidebarHeader = styled.div`
  padding: 16px 12px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SidebarHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarTitle = styled.span`
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const NewChatBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(52, 211, 153, 0.25);
  background: rgba(52, 211, 153, 0.05);
  border-radius: 10px;
  padding: 8px 12px;
  color: #34d399;
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.15s ease;
  width: 100%;

  &:hover {
    background: rgba(52, 211, 153, 0.1);
    border-color: rgba(52, 211, 153, 0.45);
  }
`;

const SidebarList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 8px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const SidebarDateLabel = styled.div`
  color: #334155;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 10px 6px 4px;
`;

const SidebarChatItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 8px;
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.08)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.2)' : 'transparent')};
  transition: all 0.12s ease;

  &:hover {
    background: ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.04)')};
  }
`;

const SidebarChatTitle = styled.span`
  flex: 1;
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const SidebarDeleteBtn = styled.button`
  appearance: none;
  border: none;
  padding: 3px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0;
  transition: all 0.12s ease;

  ${SidebarChatItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: #f87171;
    background: rgba(248, 113, 113, 0.08);
  }
`;

const SidebarEmpty = styled.div`
  color: #475569;
  font-size: 12px;
  text-align: center;
  padding: 24px 12px;
`;

const TopBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding-bottom: 8px;
  flex-shrink: 0;
`;

/** Pill action sitting alongside the EnviroChat and jurisdiction tiles. */
const ExportPdfBtn = styled.button<{ $error: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 20px;
  padding: 4px 10px 4px 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  flex-shrink: 0;
  background: ${(p) => (p.$error ? 'rgba(248, 113, 113, 0.08)' : 'rgba(255, 255, 255, 0.05)')};
  border: 1px solid
    ${(p) => (p.$error ? 'rgba(248, 113, 113, 0.28)' : 'rgba(255, 255, 255, 0.12)')};
  color: ${(p) => (p.$error ? '#fca5a5' : '#94a3b8')};
  transition:
    color 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    color: #34d399;
    background: rgba(52, 211, 153, 0.08);
    border-color: rgba(52, 211, 153, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Collapse to the icon so three tiles never crowd the model picker. */
  @media (max-width: 640px) {
    padding: 4px 7px;

    span {
      display: none;
    }
  }
`;

const SidebarToggleBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    color: #94a3b8;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1.5rem 0;
  overflow: hidden;
  min-height: 0;
`;

/** Bottom-left dock for favorites — sits left of the fixed ESI timeline. */
const HeroBottomDock = styled.div`
  width: 100%;
  margin-top: auto;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  padding: 0 clamp(1rem, 2.5vw, 2.5rem) clamp(1.25rem, 2.5vh, 1.75rem);
  box-sizing: border-box;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const Logo = styled.img`
  width: min(180px, 42vw);
  height: auto;
  margin-bottom: 28px;
  display: block;
  filter: drop-shadow(0 2px 12px rgba(0, 0, 0, 0.55));
`;

const HeadingWrap = styled.div<{ $hidden: boolean }>`
  overflow: hidden;
  max-height: ${(p) => (p.$hidden ? '0px' : '400px')};
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  margin-bottom: ${(p) => (p.$hidden ? '0' : '36px')};
  transition:
    max-height 0.5s ease,
    opacity 0.35s ease,
    margin-bottom 0.4s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Tagline = styled.p`
  margin: 0 0 14px;
  font-size: 15px;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: #34d399;
  font-weight: 500;
`;

const Heading = styled.h1`
  margin: 0 0 10px;
  color: #fafafa;
  font-family: 'Instrument Serif', Georgia, serif;
  font-size: clamp(40px, 4.5vw, 66px);
  font-weight: 400;
  line-height: 1.2;
`;

const HeadingAccent = styled.span`
  display: block;
  font-style: italic;
  color: #34d399;
`;

const SearchWrap = styled.div`
  width: 100%;
  max-width: 720px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  justify-content: center;
`;

// ─── Conversation ─────────────────────────────────────────────────────────────

const ConversationArea = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
  padding-right: 4px;
  text-align: left;
  overflow-y: auto;
  animation: ${fadeSlideIn} 0.3s ease;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const MessageRow = styled.div<{ $role: 'user' | 'system' }>`
  display: flex;
  justify-content: ${(p) => (p.$role === 'user' ? 'flex-end' : 'flex-start')};
`;

const UserBubble = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px 18px 4px 18px;
  padding: 10px 16px;
  color: #f1f5f9;
  font-size: 15px;
  line-height: 1.5;
  max-width: 80%;
  animation: ${fadeSlideIn} 0.2s ease;
`;

const SystemText = styled.div`
  color: #e2e6eb;
  font-size: 15px;
  line-height: 1.6;
  max-width: 90%;
  text-align: left;
`;

const BlinkCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 0.85em;
  background: #34d399;
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: ${blink} 0.8s step-end infinite;
`;

const StatusText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
`;

const ThinkingWave = styled.div`
  display: inline-flex;
  gap: 1px;
  padding: 2px 0;
`;

const WaveChar = styled.span<{ $delay: number }>`
  display: inline-block;
  color: #64748b;
  font-size: 15px;
  animation: ${wave} 1.4s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}s;
`;

// ─── Suggestion cards ─────────────────────────────────────────────────────────

const SuggestionsLabel = styled.p`
  color: #afb5be;
  font-size: 12px;
  margin: 0 0 8px;
  text-align: left;
`;

const SuggestionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 14px;
  animation: ${fadeSlideIn} 0.25s ease;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SuggestionCard = styled.button<{ $suggested?: boolean }>`
  appearance: none;
  text-align: left;
  border: 1px solid
    ${(p) => (p.$suggested ? 'rgba(52, 211, 153, 0.4)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(p) => (p.$suggested ? 'rgba(52, 211, 153, 0.06)' : 'rgba(255, 255, 255, 0.03)')};
  border-radius: 14px;
  padding: 12px 14px;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(52, 211, 153, 0.4);
    background: rgba(52, 211, 153, 0.09);
  }
`;

const SuggestionCardIcon = styled.div<{ $suggested?: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${(p) => (p.$suggested ? 'rgba(52, 211, 153, 0.15)' : 'rgba(255, 255, 255, 0.07)')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => (p.$suggested ? '#34d399' : '#64748b')};
  transition: all 0.15s ease;

  ${SuggestionCard}:hover & {
    background: rgba(52, 211, 153, 0.15);
    color: #34d399;
  }
`;

const SuggestionCardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuggestionCardLabel = styled.span`
  display: block;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 500;
`;

const SuggestionCardSub = styled.span`
  display: block;
  color: #a5b1c3;
  font-size: 11px;
  margin-top: 2px;
`;

const SuggestedBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: #34d399;
  color: #000;
  border-radius: 6px;
  padding: 2px 7px;
  margin-top: 6px;
`;

// ─── Chatbox ──────────────────────────────────────────────────────────────────

const SearchRow = styled.form`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: rgba(52, 211, 153, 0.5);
    box-shadow:
      0 0 0 3px rgba(52, 211, 153, 0.08),
      0 4px 24px rgba(0, 0, 0, 0.4);
  }
`;

const QueryInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  border: none;
  outline: none;
  color: #f8fafc;
  font-size: 17px;
  font-family: inherit;
  line-height: 1.4;
  padding: 16px 20px 12px;
  resize: none;
  overflow-y: auto;
  min-height: calc(1.4em + 28px);
  max-height: calc(7 * 1.4em + 28px);
  display: block;

  &::placeholder {
    color: rgba(255, 255, 255, 0.32);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const InputDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 0 14px;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px 8px 14px;
`;

const EnviroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(52, 211, 153, 0.08);
  border: 1px solid rgba(52, 211, 153, 0.2);
  border-radius: 20px;
  padding: 4px 10px 4px 7px;
  color: #34d399;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

const BadgeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 6px rgba(52, 211, 153, 0.8);
  flex-shrink: 0;
`;

const SubmitBtn = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #34d399;
  color: #000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    transform 0.1s ease;

  &:hover:not(:disabled) {
    background: #3aefb8;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

// ─── Model picker ─────────────────────────────────────────────────────────────

const ModelPickerWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ModelPickerTooltipWrap = styled.div<{ $locked: boolean }>`
  position: relative;
  flex-shrink: 0;

  ${(p) =>
    p.$locked &&
    `
    &:hover::after {
      content: "Model is locked after the first message";
      position: absolute;
      bottom: calc(100% + 8px);
      right: 0;
      white-space: nowrap;
      background: rgba(10, 16, 24, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      padding: 6px 10px;
      font-size: 11px;
      color: #94a3b8;
      pointer-events: none;
      z-index: 110;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
    }
  `}
`;

const ModelPickerBtn = styled.button<{ $open: boolean }>`
  appearance: none;
  border: 1px solid ${(p) => (p.$open ? 'rgba(52, 211, 153, 0.5)' : 'rgba(255, 255, 255, 0.14)')};
  background: ${(p) => (p.$open ? 'rgba(52, 211, 153, 0.1)' : 'rgba(255, 255, 255, 0.05)')};
  border-radius: 20px;
  padding: 4px 10px 4px 12px;
  color: ${(p) => (p.$open ? '#34d399' : '#94a3b8')};
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  svg {
    transition: transform 0.2s ease;
    transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
    opacity: 0.6;
  }

  &:hover {
    border-color: rgba(52, 211, 153, 0.35);
    color: #34d399;
    background: rgba(52, 211, 153, 0.06);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ModelPanel = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 360px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  padding: 18px 16px 14px;
  text-align: left;
  background: rgba(10, 16, 24, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(16px);
  animation: ${fadeIn} 0.15s ease;
  z-index: 100;
`;

const ModelPanelTitle = styled.h3`
  margin: 0 4px 10px;
  color: #f1f5f9;
  font-size: 17px;
  font-weight: 500;
`;

const ModelRadio = styled.span<{ $active: boolean; $size: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  box-sizing: border-box;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid ${(p) => (p.$active ? '#34d399' : 'rgba(255, 255, 255, 0.35)')};
  background: ${(p) => (p.$active ? '#6ee7b7' : 'transparent')};
  box-shadow: ${(p) =>
    p.$active ? 'inset 0 0 0 3px #0a1018, 0 0 10px rgba(52, 211, 153, 0.45)' : 'none'};
  transition: all 0.15s ease;
`;

const ModelOptionBtn = styled.button`
  appearance: none;
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 10px;
  padding: 9px 8px;
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: rgba(52, 211, 153, 0.06);
  }

  &:hover ${ModelRadio} {
    border-color: #34d399;
  }
`;

const ModelOptionText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ModelOptionLabel = styled.span<{ $strong?: boolean }>`
  color: #f1f5f9;
  font-size: ${(p) => (p.$strong ? '15px' : '14px')};
  font-weight: ${(p) => (p.$strong ? 600 : 400)};
`;

const ModelOptionDesc = styled.span`
  color: #94a3b8;
  font-size: 12px;
`;

const ModelOptionVendor = styled.span`
  color: #64748b;
  font-size: 12px;
  flex-shrink: 0;
`;

const ModelSectionDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 10px 4px 12px;
`;

const ModelSectionLabel = styled.div<{ $accent?: boolean }>`
  margin: 0 4px 6px;
  color: ${(p) => (p.$accent ? '#34d399' : '#94a3b8')};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const ModelSectionBlurb = styled.p`
  margin: 0 4px 6px;
  color: #94a3b8;
  font-size: 12.5px;
  line-height: 1.5;

  span {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const LemCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border: 1px solid rgba(52, 211, 153, 0.45);
  border-radius: 14px;
  background: rgba(52, 211, 153, 0.06);
  cursor: default;
`;

const LemIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #6ee7b7;
  color: #0a1018;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LemBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const LemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const LemName = styled.div`
  color: #e2e8f0;
  font-size: 14px;
  line-height: 1.35;

  strong {
    display: block;
    color: #f1f5f9;
    font-size: 15px;
    font-weight: 600;
  }
`;

const LemBadge = styled.span`
  flex-shrink: 0;
  border: 1px solid rgba(251, 191, 36, 0.6);
  border-radius: 20px;
  padding: 2px 8px;
  color: #fbbf24;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const LemDesc = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
`;

const ModelPanelFootnote = styled.div`
  margin: 10px 4px 0;
  color: #64748b;
  font-size: 11px;
`;

// ─── File preview ─────────────────────────────────────────────────────────────

const FilePreviewBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px 4px;
`;

const FilePreviewChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 4px 6px 4px 8px;
  max-width: 160px;
`;

const PreviewThumb = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

const FileChipLabel = styled.span`
  color: #cbd5e1;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const RemoveChipBtn = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  background: transparent;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 4px;
  transition: color 0.12s ease;

  &:hover {
    color: #e2e8f0;
  }
`;

const AttachBtn = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  &:hover:not(:disabled) {
    color: #34d399;
    background: rgba(52, 211, 153, 0.08);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const UserBubbleFiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

const MsgThumb = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
`;

const MsgFileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 3px 8px 3px 5px;
  font-size: 11px;
  color: #94a3b8;
`;

// ─── Delete confirmation modal ────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const ModalPanel = styled.div`
  width: min(360px, calc(100vw - 32px));
  background: linear-gradient(180deg, rgba(12, 18, 26, 0.97) 0%, rgba(8, 13, 20, 0.99) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.7);
  animation: ${fadeSlideIn} 0.18s ease;
`;

const ModalTitle = styled.h3`
  margin: 0 0 8px;
  color: #f1f5f9;
  font-size: 16px;
  font-weight: 600;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
`;

const ModalBody = styled.p`
  margin: 0 0 20px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.55;
`;

const ModalChatName = styled.span`
  color: #e2e8f0;
  font-weight: 500;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalCancelBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 8px 16px;
  color: #94a3b8;
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.09);
    color: #e2e8f0;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ModalDeleteBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(248, 113, 113, 0.35);
  background: rgba(248, 113, 113, 0.08);
  border-radius: 10px;
  padding: 8px 16px;
  min-width: 70px;
  color: #f87171;
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(248, 113, 113, 0.16);
    border-color: rgba(248, 113, 113, 0.55);
    color: #fca5a5;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

// ─── Shortcut row ─────────────────────────────────────────────────────────────

const ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  justify-content: center;
`;

const ShortcutBtn = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 13px 7px 10px;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.35)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(p) => (p.$active ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.04)')};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(52, 211, 153, 0.35);
    background: rgba(52, 211, 153, 0.07);
  }

  svg {
    color: ${(p) => (p.$active ? '#34d399' : '#94a3b8')};
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  &:hover svg {
    color: #34d399;
  }
`;

const ShortcutLabel = styled.span<{ $active?: boolean }>`
  color: ${(p) => (p.$active ? '#e2e8f0' : '#cbd5e1')};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? '500' : '400')};
  white-space: nowrap;
  transition: color 0.15s ease;

  ${ShortcutBtn}:hover & {
    color: #e2e8f0;
  }
`;

const ShortcutSub = styled.span`
  color: #a2afc3;
  font-size: 11px;
  font-weight: 400;
  margin-left: 2px;
`;

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface ClassifyResponse {
  intents?: ClassifiedIntent[];
  primary_intent?: string;
  message?: string;
}

interface ClassifiedIntent {
  confidence: number;
  intent: string;
  reasoning: string;
}

interface DisambiguationOption {
  confidence: number;
  intent: string;
  reasoning: string;
}

interface AttachFile {
  preview: string;
  name: string;
  type: string;
}

interface ConversationMessage {
  id: string;
  role: 'user' | 'system';
  content: string;
  attachFiles?: AttachFile[];
}

interface ActiveSuggestions {
  suggested: string;
  options: DisambiguationOption[];
}

interface ChatListItem {
  id: string;
  title: string;
  updated_at: string;
}

const FALLBACK_OPTIONS: DisambiguationOption[] = Object.keys(INTENT_META).map((intent) => ({
  confidence: 0,
  intent,
  reasoning: '',
}));

const normalizeIntents = (data: ClassifyResponse): DisambiguationOption[] => {
  const seen = new Set<string>();
  const intents = Array.isArray(data.intents) ? data.intents : [];
  return intents
    .filter((item): item is ClassifiedIntent => {
      if (!item || !item.intent || !INTENT_ROUTES[item.intent] || seen.has(item.intent))
        return false;
      seen.add(item.intent);
      return true;
    })
    .sort((a, b) => b.confidence - a.confidence)
    .map((item) => ({
      confidence: item.confidence,
      intent: item.intent,
      reasoning: item.reasoning || '',
    }));
};

const getSuggestedIntent = (options: DisambiguationOption[], primaryIntent?: string): string => {
  if (!options.length) return '';
  const top = options[0].confidence;
  const topOptions = options.filter((o) => o.confidence === top);
  if (primaryIntent && topOptions.some((o) => o.intent === primaryIntent)) return primaryIntent;
  return topOptions[0].intent;
};

const groupChatsByDate = (chats: ChatListItem[]): { label: string; items: ChatListItem[] }[] => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const map = new Map<string, ChatListItem[]>();
  chats.forEach((chat) => {
    const d = new Date(chat.updated_at);
    let label: string;
    if (d.toDateString() === today.toDateString()) label = 'Today';
    else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
    else label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(chat);
  });
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
};

// ─── Component ────────────────────────────────────────────────────────────────

const EnviroChat: React.FC<{ context: IAuthContext }> = ({ context }) => {
  const [query, setQuery] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [activeSuggestions, setActiveSuggestions] = useState<ActiveSuggestions | null>(null);
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelId>('auto');
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<AttachFile[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Chat history state
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [activeChatTitle, setActiveChatTitle] = useState('');
  const [chatList, setChatList] = useState<ChatListItem[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatListLoading, setChatListLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [deletePendingId, setDeletePendingId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const history = useHistory();
  const location = useLocation();
  const { id: urlChatId } = useParams<{ id?: string }>();
  const isMountedRef = useRef(true);
  const modelPickerRef = useRef<HTMLDivElement>(null);
  const streamBufferRef = useRef<{
    text: string;
    flushTimer: ReturnType<typeof setTimeout> | null;
  }>({ text: '', flushTimer: null });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFilesRef = useRef<File[]>([]);

  const groupedChats = useMemo(() => groupChatsByDate(chatList), [chatList]);

  const jurisdictionId = useSelector<{ jurisdiction: IJurisdictionState }, JurisdictionId>(
    (s) => s.jurisdiction.activeId
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (streamBufferRef.current.flushTimer) clearTimeout(streamBufferRef.current.flushTimer);
    };
  }, []);

  useEffect(() => {
    if (context.isAuthenticated) loadChatList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.isAuthenticated]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node)) {
        setModelMenuOpen(false);
      }
    };
    if (modelMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [modelMenuOpen]);

  useEffect(() => {
    if (!streamingMsgId) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [streamingMsgId]);

  useEffect(() => {
    if (!query && inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }, [query]);

  // Deep-link: load a specific chat from the URL param, or redirect unauthenticated users with bounce-back
  useEffect(() => {
    if (!urlChatId) return;
    if (!context.isInitialized) return;
    if (!context.isAuthenticated) {
      history.replace(`/login?redirect=${encodeURIComponent(`/enviro-chat/${urlChatId}`)}`);
      return;
    }
    loadChat(urlChatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChatId, context.isAuthenticated, context.isInitialized]);

  // Clear state when navigating back to /enviro-chat (no ID param)
  useEffect(() => {
    if (urlChatId) return;
    setMessages([]);
    setActiveChatId(null);
    setActiveChatTitle('');
    setActiveSuggestions(null);
    setSelectedModel('auto');
    setQuery('');
    setUploadedFiles([]);
    setFilePreviews([]);
    pendingFilesRef.current = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChatId]);

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const appendAttachmentFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) return;
    const updatedFiles = [...uploadedFiles, ...selectedFiles];
    setUploadedFiles(updatedFiles);
    const previews = await Promise.all(
      updatedFiles.map(async (file) => {
        if (IMAGE_MIME_TYPES.includes(file.type)) {
          const base64 = await blobToBase64(file);
          return { preview: base64, name: file.name, type: file.type };
        }
        return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
      })
    );
    setFilePreviews(previews);
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []) as File[];
    e.target.value = '';
    // The picker's `accept` filter is advisory — users can switch the dialog to
    // "All files" — so validate here too and keep both paths in agreement.
    const { accepted, rejected } = partitionFiles(selectedFiles);
    notifyRejectedFiles(rejected);
    await appendAttachmentFiles(accepted);
  };

  const removeAttachment = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  // Drop anywhere on the page to attach. Mirrors the paperclip button's
  // disabled condition so a drop can't sneak an attachment in mid-stream.
  const { isDragging } = useFileDrop({
    onFiles: appendAttachmentFiles,
    onReject: notifyRejectedFiles,
    disabled: isThinking || chatLoading || !!streamingMsgId,
  });

  // ─── Chat history API calls ──────────────────────────────────────────────────

  const loadChatList = async () => {
    setChatListLoading(true);
    try {
      const token = await context.user?.getIdToken();
      if (!token) return;
      const res = await fetch(`${CERES_API}/api/enviro-chats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setChatList(await res.json());
    } finally {
      setChatListLoading(false);
    }
  };

  const loadChat = async (chatId: string) => {
    if (chatId === activeChatId) return;
    setChatLoading(true);
    setMessages([]);
    setActiveSuggestions(null);
    try {
      const token = await context.user?.getIdToken();
      if (!token) return;
      const res = await fetch(`${CERES_API}/api/enviro-chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setMessages(data.messages ?? []);
      setActiveSuggestions(data.suggestions ?? null);
      setActiveChatId(chatId);
      setActiveChatTitle(data.title ?? '');
      setSelectedModel((data.model as ModelId) ?? 'auto');
      setQuery('');
      pendingFilesRef.current = [];
      inputRef.current?.focus();
    } finally {
      setChatLoading(false);
    }
  };

  const saveChat = async (
    chatId: string,
    title: string,
    currentMessages: ConversationMessage[],
    suggestions: ActiveSuggestions | null,
    model: string,
    region: string
  ): Promise<boolean> => {
    if (!context.isAuthenticated) return false;
    try {
      const token = await context.user?.getIdToken();
      if (!token) return false;
      const stripped = currentMessages.map((m) => ({
        ...m,
        attachFiles: m.attachFiles?.map((f) => ({ name: f.name, type: f.type, preview: '' })),
      }));
      await fetch(`${CERES_API}/api/enviro-chats/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, title, messages: stripped, suggestions, model, region }),
      });
      setChatList((prev) => {
        const entry: ChatListItem = { id: chatId, title, updated_at: new Date().toISOString() };
        return prev.some((c) => c.id === chatId)
          ? prev.map((c) => (c.id === chatId ? entry : c))
          : [entry, ...prev];
      });
      return true;
    } catch {
      return false;
    }
  };

  const deleteChat = async (chatId: string) => {
    setDeleteLoading(true);
    try {
      const token = await context.user?.getIdToken();
      if (!token) return;
      await fetch(`${CERES_API}/api/enviro-chats/${chatId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setChatList((prev) => prev.filter((c) => c.id !== chatId));
      if (activeChatId === chatId) {
        setMessages([]);
        setActiveChatId(null);
        setActiveChatTitle('');
        setActiveSuggestions(null);
      }
      setDeletePendingId(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const startNewChat = () => {
    if (!urlChatId) {
      inputRef.current?.focus();
      return;
    }
    history.push('/enviro-chat');
  };

  // ─── Export to PDF ───────────────────────────────────────────────────────────

  const handleExportPdf = async () => {
    if (exportingPdf || !messages.length) return;
    setExportingPdf(true);
    setPdfError(null);

    try {
      const transcript: PdfChatMessage[] = messages.map((msg) => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content,
        // Saved chats drop their previews, so attachments without one fall back
        // to a filename chip in the PDF.
        attachments: msg.attachFiles?.map((f) => ({
          name: f.name,
          type: f.type,
          dataUrl: f.preview || undefined,
        })),
      }));

      await exportChatTranscriptToPdf({
        title: activeChatTitle || lastUserQuery || 'Enviro Chat transcript',
        messages: transcript,
        meta: [
          { value: formatDisplayDate(new Date()) },
          { value: findJurisdiction(jurisdictionId).name },
          { value: MODELS.find((m) => m.id === selectedModel)?.label ?? 'Auto' },
        ],
      });
    } catch (err) {
      // Surfaced on the button itself (label + tooltip) to keep the toolbar tight.
      setPdfError('Could not create the PDF. Click to try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const redirectToIntent = (intent: string, q: string) => {
    const route = INTENT_ROUTES[intent];
    if (!route) return;
    const modelParam = selectedModel !== 'auto' ? `&model=${selectedModel}` : '';
    const state = pendingFilesRef.current.length ? { files: pendingFilesRef.current } : undefined;
    history.push(`${route}?q=${encodeURIComponent(q)}${modelParam}`, state);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q || isThinking || !!streamingMsgId) return;

    if (!context.isAuthenticated) {
      history.push(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }

    setActiveSuggestions(null);
    setStatusMessage(null);
    setQuery('');

    const modelSnapshot = selectedModel;
    const regionSnapshot = jurisdictionId;
    const snapshotFiles = [...uploadedFiles];
    const snapshotPreviews = [...filePreviews];
    pendingFilesRef.current = snapshotFiles;
    setUploadedFiles([]);
    setFilePreviews([]);

    const messagesSnapshot = [...messages];

    const isNewChat = !activeChatId;
    const currentChatId = activeChatId ?? generateChatId();
    const currentChatTitle = activeChatTitle || q.slice(0, 60);
    if (isNewChat) {
      setActiveChatId(currentChatId);
      setActiveChatTitle(currentChatTitle);
      history.push(`/enviro-chat/${currentChatId}`);
    }

    const chatHistory = messagesSnapshot.map((m) => ({
      role: m.role === 'system' ? 'assistant' : 'user',
      content: m.content,
    }));

    const userMsgId = `user-${Date.now()}`;
    const systemMsgId = `system-${Date.now()}`;

    const userOnlyMessages: ConversationMessage[] = [
      ...messagesSnapshot,
      { id: userMsgId, role: 'user', content: q, attachFiles: snapshotPreviews },
    ];
    setMessages([...userOnlyMessages, { id: systemMsgId, role: 'system', content: '' }]);
    saveChat(currentChatId, currentChatTitle, userOnlyMessages, null, modelSnapshot, regionSnapshot).then(
      (saved) => {
        if (isNewChat && !saved) history.replace('/enviro-chat');
      }
    );
    setIsThinking(true);

    try {
      const token = await context.user?.getIdToken();
      const modelId = MODEL_API_IDS[modelSnapshot] ?? MODEL_API_IDS.auto;
      const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      const formData = new FormData();
      formData.append('q', q);
      formData.append('model', modelId);
      formData.append('region', regionSnapshot);
      formData.append('history', JSON.stringify(chatHistory));
      formData.append('chatId', currentChatId);
      if (snapshotFiles.length) {
        snapshotFiles.forEach((file) => formData.append('files', file));
      }

      const chatRoute = CHAT_ROUTE;
      const res = await fetch(`${CLASSIFY_API}${chatRoute}`, {
        method: 'POST',
        headers: authHeader,
        body: formData,
      });

      if (!res.ok) {
        if (res.status === 413) throw new Error('FILE_TOO_LARGE');
        throw new Error(`HTTP ${res.status}`);
      }

      setIsThinking(false);
      setStreamingMsgId(systemMsgId);
      streamBufferRef.current = { text: '', flushTimer: null };
      inputRef.current?.focus();

      const flush = () => {
        if (streamBufferRef.current.flushTimer) {
          clearTimeout(streamBufferRef.current.flushTimer);
          streamBufferRef.current.flushTimer = null;
        }
        const text = streamBufferRef.current.text;
        setMessages((prev) =>
          prev.map((m) => (m.id === systemMsgId ? { ...m, content: text } : m))
        );
      };

      const scheduleFlush = () => {
        if (streamBufferRef.current.flushTimer) return;
        streamBufferRef.current.flushTimer = setTimeout(() => {
          streamBufferRef.current.flushTimer = null;
          const text = streamBufferRef.current.text;
          setMessages((prev) =>
            prev.map((m) => (m.id === systemMsgId ? { ...m, content: text } : m))
          );
        }, 50);
      };

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let carryOver = '';
      let resolvedSuggestions: ActiveSuggestions | null = null;
      let streamDone = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!isMountedRef.current) break;

        const chunk = carryOver + decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        carryOver = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event: any;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          if (event.type === 'status') {
            setStatusMessage(event.message ?? null);
          } else if (event.type === 'meta') {
            const options = normalizeIntents(event as ClassifyResponse);
            const suggested = getSuggestedIntent(options, event.primary_intent);
            resolvedSuggestions = {
              suggested,
              options: options.length > 0 ? options : FALLBACK_OPTIONS,
            };
          } else if (event.type === 'token') {
            streamBufferRef.current.text += event.content;
            scheduleFlush();
            setStatusMessage(null);
          } else if (event.type === 'done') {
            streamDone = true;
            flush();
            setStreamingMsgId(null);
            setStatusMessage(null);
            if (resolvedSuggestions) setActiveSuggestions(resolvedSuggestions);
            const finalMessages: ConversationMessage[] = [
              ...userOnlyMessages,
              { id: systemMsgId, role: 'system', content: streamBufferRef.current.text },
            ];
            saveChat(
              currentChatId,
              currentChatTitle,
              finalMessages,
              resolvedSuggestions,
              modelSnapshot,
              regionSnapshot
            );
          } else if (event.type === 'error') {
            streamDone = true;
            setStatusMessage(null);
            const errText = event.message ?? 'Something went wrong. Please pick a tool below.';
            streamBufferRef.current.text = errText;
            flush();
            setStreamingMsgId(null);
            const errorSuggestions: ActiveSuggestions = {
              suggested: '',
              options: FALLBACK_OPTIONS,
            };
            setActiveSuggestions(errorSuggestions);
            const errMessages: ConversationMessage[] = [
              ...userOnlyMessages,
              { id: systemMsgId, role: 'system', content: errText },
            ];
            saveChat(currentChatId, currentChatTitle, errMessages, errorSuggestions, modelSnapshot, regionSnapshot);
          }
        }
      }

      // Stream closed without a done/error event (e.g. connection dropped)
      if (!streamDone && isMountedRef.current) {
        flush();
        setStreamingMsgId(null);
        const finalMessages: ConversationMessage[] = [
          ...userOnlyMessages,
          { id: systemMsgId, role: 'system', content: streamBufferRef.current.text },
        ];
        saveChat(
          currentChatId,
          currentChatTitle,
          finalMessages,
          resolvedSuggestions,
          modelSnapshot,
          regionSnapshot
        );
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setIsThinking(false);
      setStreamingMsgId(null);
      if (streamBufferRef.current.flushTimer) {
        clearTimeout(streamBufferRef.current.flushTimer);
        streamBufferRef.current.flushTimer = null;
      }
      const errText =
        err?.message === 'FILE_TOO_LARGE'
          ? 'File is too large. Please try a smaller file.'
          : 'Something went wrong. Please pick a tool below.';
      const errorSuggestions: ActiveSuggestions = { suggested: '', options: FALLBACK_OPTIONS };
      setMessages((prev) =>
        prev.map((m) => (m.id === systemMsgId ? { ...m, content: errText } : m))
      );
      setActiveSuggestions(errorSuggestions);
      const errMessages: ConversationMessage[] = [
        ...userOnlyMessages,
        { id: systemMsgId, role: 'system', content: errText },
      ];
      saveChat(currentChatId, currentChatTitle, errMessages, errorSuggestions, modelSnapshot, regionSnapshot);
    }
  };

  const lastUserQuery = [...messages].reverse().find((m) => m.role === 'user')?.content ?? '';
  const hasConversation = messages.length > 0;

  return (
    <PageContainer>
      <FileDropOverlay visible={isDragging} />
      <EnviroHeroChrome />
      <MainArea>
        <Sidebar $open={sidebarOpen}>
          <SidebarInner>
            <SidebarHeader>
              <SidebarHeaderRow>
                <SidebarTitle>Chat History</SidebarTitle>
                <SidebarToggleBtn
                  type="button"
                  onClick={() => setSidebarOpen((o) => !o)}
                  title="Close history"
                >
                  <ChevronLeft size={15} />
                </SidebarToggleBtn>
              </SidebarHeaderRow>
              <NewChatBtn type="button" onClick={startNewChat}>
                <Plus size={14} />
                New Chat
              </NewChatBtn>
            </SidebarHeader>
            <SidebarList>
              {chatListLoading ? (
                <SidebarEmpty>Loading…</SidebarEmpty>
              ) : groupedChats.length === 0 ? (
                <SidebarEmpty>No saved chats yet</SidebarEmpty>
              ) : (
                groupedChats.map((group) => (
                  <React.Fragment key={group.label}>
                    <SidebarDateLabel>{group.label}</SidebarDateLabel>
                    {group.items.map((chat) => (
                      <SidebarChatItem
                        key={chat.id}
                        $active={chat.id === activeChatId}
                        onClick={() => history.push(`/enviro-chat/${chat.id}`)}
                      >
                        <SidebarChatTitle title={chat.title}>{chat.title}</SidebarChatTitle>
                        <SidebarDeleteBtn
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletePendingId(chat.id);
                          }}
                          title="Delete chat"
                        >
                          <Trash2 size={12} />
                        </SidebarDeleteBtn>
                      </SidebarChatItem>
                    ))}
                  </React.Fragment>
                ))
              )}
            </SidebarList>
          </SidebarInner>
        </Sidebar>

        <HeroContent>
          <TopBar>
            {!sidebarOpen && (
              <SidebarToggleBtn
                type="button"
                onClick={() => setSidebarOpen((o) => !o)}
                title="Open history"
              >
                <ChevronRight size={15} />
              </SidebarToggleBtn>
            )}
          </TopBar>

          <Logo src={HeaderEnviroAILogo} alt="enviro.ai" />

          <HeadingWrap $hidden={hasConversation || chatLoading}>
            <Tagline>Climbing toward environmental superintelligence</Tagline>
            <Heading>
              Ask anything about
              <HeadingAccent>your environment</HeadingAccent>
            </Heading>
          </HeadingWrap>

          <SearchWrap>
            {chatLoading && (
              <ConversationArea style={{ alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} style={{ color: '#34d399' }} />
              </ConversationArea>
            )}
            {!chatLoading && hasConversation && (
              <ConversationArea>
                {messages.map((msg) => (
                  <MessageRow key={msg.id} $role={msg.role}>
                    {msg.role === 'user' ? (
                      <UserBubble>
                        {msg.attachFiles && msg.attachFiles.length > 0 && (
                          <UserBubbleFiles>
                            {msg.attachFiles.map((f, i) =>
                              IMAGE_MIME_TYPES.includes(f.type) && f.preview ? (
                                <MsgThumb key={i} src={f.preview} alt={f.name} />
                              ) : (
                                <MsgFileChip key={i}>
                                  <Paperclip size={11} />
                                  {f.name}
                                </MsgFileChip>
                              )
                            )}
                          </UserBubbleFiles>
                        )}
                        {msg.content}
                      </UserBubble>
                    ) : (
                      <SystemText>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ href, children }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer">
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                        {streamingMsgId === msg.id && <BlinkCursor />}
                      </SystemText>
                    )}
                  </MessageRow>
                ))}

                {(isThinking || !!statusMessage) && (
                  <MessageRow $role="system">
                    {statusMessage ? (
                      <StatusText>
                        <CircularProgress size={13} style={{ color: '#64748b', flexShrink: 0 }} />
                        {statusMessage}
                      </StatusText>
                    ) : (
                      <ThinkingWave>
                        {'Thinking...'.split('').map((char, i) => (
                          <WaveChar key={i} $delay={i * 0.06}>
                            {char}
                          </WaveChar>
                        ))}
                      </ThinkingWave>
                    )}
                  </MessageRow>
                )}
                <div />
              </ConversationArea>
            )}

            {activeSuggestions && (
              <>
                <SuggestionsLabel>{SUGGESTIONS_LABEL}</SuggestionsLabel>
                <SuggestionsGrid>
                  {activeSuggestions.options.map(({ intent }) => {
                    const meta = INTENT_META[intent];
                    const Icon = INTENT_ICONS[intent];
                    const isSuggested = intent === activeSuggestions.suggested;
                    if (!meta) return null;
                    return (
                      <SuggestionCard
                        key={intent}
                        type="button"
                        $suggested={isSuggested}
                        onClick={() => redirectToIntent(intent, lastUserQuery)}
                      >
                        <SuggestionCardIcon $suggested={isSuggested}>
                          {Icon && <Icon size={14} />}
                        </SuggestionCardIcon>
                        <SuggestionCardBody>
                          <SuggestionCardLabel>{meta.label}</SuggestionCardLabel>
                          <SuggestionCardSub>{meta.subtitle}</SuggestionCardSub>
                          {isSuggested && <SuggestedBadge>Suggested</SuggestedBadge>}
                        </SuggestionCardBody>
                      </SuggestionCard>
                    );
                  })}
                </SuggestionsGrid>
              </>
            )}

            <SearchRow onSubmit={handleSubmit}>
              <QueryInput
                ref={inputRef}
                rows={1}
                placeholder="What do you need help with today?"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                autoFocus
                disabled={isThinking || chatLoading || !!streamingMsgId}
              />
              {filePreviews.length > 0 && (
                <FilePreviewBar>
                  {filePreviews.map((f, i) => (
                    <FilePreviewChip key={i}>
                      {IMAGE_MIME_TYPES.includes(f.type) ? (
                        <PreviewThumb src={f.preview} alt={f.name} />
                      ) : (
                        <Paperclip size={12} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      )}
                      <FileChipLabel>{f.name}</FileChipLabel>
                      <RemoveChipBtn type="button" onClick={() => removeAttachment(i)}>
                        <X size={12} />
                      </RemoveChipBtn>
                    </FilePreviewChip>
                  ))}
                </FilePreviewBar>
              )}
              <InputDivider />
              <ToolbarRow>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AttachBtn
                    type="button"
                    onClick={triggerFileInput}
                    disabled={isThinking || chatLoading || !!streamingMsgId}
                    title="Attach image"
                  >
                    <Paperclip size={16} />
                  </AttachBtn>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="file-input-enviro-chat"
                    style={{ display: 'none' }}
                    multiple
                    accept={ACCEPT_ATTR}
                    onChange={handleFileInputChange}
                  />
                  <EnviroBadge>
                    <BadgeDot />
                    EnviroChat
                  </EnviroBadge>
                  <JurisdictionChip />
                  {hasConversation && !chatLoading && (
                    <ExportPdfBtn
                      type="button"
                      $error={!!pdfError}
                      onClick={handleExportPdf}
                      disabled={exportingPdf || isThinking || !!streamingMsgId}
                      title={
                        pdfError ||
                        (isThinking || streamingMsgId
                          ? 'Available once the answer finishes'
                          : 'Download this chat as a PDF')
                      }
                    >
                      {exportingPdf ? (
                        <CircularProgress size={12} style={{ color: '#94a3b8' }} />
                      ) : (
                        <Printer size={13} />
                      )}
                      <span>
                        {exportingPdf ? 'Preparing…' : pdfError ? 'Export failed' : 'Export to PDF'}
                      </span>
                    </ExportPdfBtn>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ModelPickerTooltipWrap $locked={hasConversation}>
                    <ModelPickerWrap ref={modelPickerRef}>
                      <ModelPickerBtn
                        type="button"
                        $open={modelMenuOpen}
                        onClick={() => setModelMenuOpen((o) => !o)}
                        disabled={hasConversation || isThinking || chatLoading || !!streamingMsgId}
                      >
                        {MODELS.find((m) => m.id === selectedModel)?.label ?? 'Auto'}
                        <ChevronDown size={13} />
                      </ModelPickerBtn>
                      {modelMenuOpen && (
                        <ModelPanel role="dialog" aria-label="Choose your intelligence">
                          <ModelPanelTitle>Choose your intelligence</ModelPanelTitle>
                          <div role="radiogroup" aria-label="Model">
                            <ModelOptionBtn
                              type="button"
                              role="radio"
                              aria-checked={selectedModel === AUTO_MODEL.id}
                              onClick={() => {
                                setSelectedModel(AUTO_MODEL.id);
                                setModelMenuOpen(false);
                              }}
                            >
                              <ModelRadio $active={selectedModel === AUTO_MODEL.id} $size={22} />
                              <ModelOptionText>
                                <ModelOptionLabel $strong>{AUTO_MODEL.label}</ModelOptionLabel>
                                <ModelOptionDesc>{AUTO_MODEL.description}</ModelOptionDesc>
                              </ModelOptionText>
                            </ModelOptionBtn>

                            <ModelSectionDivider />
                            <ModelSectionLabel>Frontier models + EnviroAI knowledge</ModelSectionLabel>
                            <ModelSectionBlurb>
                              Powered by frontier AI, grounded in EnviroAI&rsquo;s <span>20M+</span>{' '}
                              environmental documents through retrieval.
                            </ModelSectionBlurb>
                            {FRONTIER_MODELS.map((m) => (
                              <ModelOptionBtn
                                key={m.id}
                                type="button"
                                role="radio"
                                aria-checked={selectedModel === m.id}
                                onClick={() => {
                                  setSelectedModel(m.id);
                                  setModelMenuOpen(false);
                                }}
                              >
                                <ModelRadio $active={selectedModel === m.id} $size={18} />
                                <ModelOptionText>
                                  <ModelOptionLabel>{m.label}</ModelOptionLabel>
                                </ModelOptionText>
                                <ModelOptionVendor>{m.description}</ModelOptionVendor>
                              </ModelOptionBtn>
                            ))}
                          </div>

                          <ModelSectionDivider />
                          <ModelSectionLabel $accent>EnviroAI&rsquo;s proprietary model</ModelSectionLabel>
                          <LemCard aria-disabled="true">
                            <LemIcon>ai</LemIcon>
                            <LemBody>
                              <LemHeader>
                                <LemName>
                                  EnviroAI
                                  <strong>Large Environmental Model</strong>
                                </LemName>
                                <LemBadge>In development</LemBadge>
                              </LemHeader>
                              <LemDesc>Our proprietary environmental model.</LemDesc>
                            </LemBody>
                          </LemCard>
                          <ModelPanelFootnote>Concept preview • LEM not yet available</ModelPanelFootnote>
                        </ModelPanel>
                      )}
                    </ModelPickerWrap>
                  </ModelPickerTooltipWrap>
                  <SubmitBtn
                    type="submit"
                    disabled={!query.trim() || isThinking || chatLoading || !!streamingMsgId}
                  >
                    {isThinking ? (
                      <CircularProgress size={18} style={{ color: '#000' }} />
                    ) : (
                      <Send size={18} />
                    )}
                  </SubmitBtn>
                </div>
              </ToolbarRow>
            </SearchRow>
            <ShortcutRow>
              <ShortcutBtn type="button" onClick={() => history.push('/permit-agent')}>
                <Layers size={14} />
                <ShortcutLabel>Permit Agent</ShortcutLabel>
              </ShortcutBtn>
              <ShortcutBtn type="button" onClick={() => history.push('/search')}>
                <BookOpen size={14} />
                <ShortcutLabel>
                  Enviro AI Search<ShortcutSub>11M+ documents</ShortcutSub>
                </ShortcutLabel>
              </ShortcutBtn>
              <ShortcutBtn type="button" onClick={() => history.push('/enviro-deep-research')}>
                <Search size={14} />
                <ShortcutLabel>Enviro Deep Search</ShortcutLabel>
              </ShortcutBtn>
              <ShortcutBtn type="button" onClick={() => history.push('/myenviroai')}>
                <GitBranch size={14} />
                <ShortcutLabel>EnviroMulti-Model</ShortcutLabel>
              </ShortcutBtn>
            </ShortcutRow>
          </SearchWrap>

          {!hasConversation && (
            <HeroBottomDock>
              <RecentFavoriteSites
                context={context}
                maxItems={3}
                placement="page-bottom"
                clearEsiOverlap
              />
            </HeroBottomDock>
          )}
        </HeroContent>
      </MainArea>
      {deletePendingId &&
        (() => {
          const chatToDelete = chatList.find((c) => c.id === deletePendingId);
          return (
            <ModalOverlay
              onClick={() => {
                if (!deleteLoading) setDeletePendingId(null);
              }}
            >
              <ModalPanel onClick={(e) => e.stopPropagation()}>
                <ModalTitle>Delete chat?</ModalTitle>
                <ModalBody>
                  <ModalChatName>"{chatToDelete?.title ?? 'This chat'}"</ModalChatName> will be
                  permanently deleted and cannot be recovered.
                </ModalBody>
                <ModalActions>
                  <ModalCancelBtn
                    type="button"
                    onClick={() => setDeletePendingId(null)}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </ModalCancelBtn>
                  <ModalDeleteBtn
                    type="button"
                    onClick={() => deleteChat(deletePendingId)}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? (
                      <CircularProgress size={13} style={{ color: '#f87171' }} />
                    ) : (
                      'Delete'
                    )}
                  </ModalDeleteBtn>
                </ModalActions>
              </ModalPanel>
            </ModalOverlay>
          );
        })()}
    </PageContainer>
  );
};

export default withAuthContext(EnviroChat);
